// The routes of the QOR Console (the Razor pages' paths, detail pages with the id in the path as before).
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BffError, ForgotPasswordPage, LoginPage, LogoutPage, SESSION_KEY } from '@qor/web-shared';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ConsoleLayout } from './components/ConsoleLayout';
import { HomeRedirect, RequireInternal, RequireStaff } from './components/RequireStaff';
import { AddCardholderPage } from './pages/AddCardholder';
import { AuditLogPage } from './pages/AuditLog';
import { InterCashAccountPage } from './pages/InterCashAccount';
import { FundingPage } from './pages/Funding';
import { LoadsPage } from './pages/Loads';
import { ProcessorLogPage } from './pages/ProcessorLog';
import { SecurityPage } from './pages/Security';
import { BulkAddCardholdersPage } from './pages/BulkAddCardholders';
import { CardholderDetailPage } from './pages/CardholderDetail';
import { CardholdersPage } from './pages/Cardholders';
import { DashboardPage } from './pages/Dashboard';
import { GscKioskPage } from './pages/GscKiosk';
import { PrivacyPage, SupportPage, TermsPage } from './pages/Legal';
import { MdpIntegrationPage } from './pages/MdpIntegration';
import { PartnerDetailPage } from './pages/PartnerDetail';
import { PartnersPage } from './pages/Partners';
import { PointsConfigurationPage } from './pages/PointsConfiguration';
import { PointsToCashPage } from './pages/PointsToCash';
import { PointsToCashDetailPage } from './pages/PointsToCashDetail';
import { TransactionsPage } from './pages/Transactions';
import { UserDetailPage } from './pages/UserDetail';
import { UsersPage } from './pages/Users';

/** A 401 from the BFF means the session is gone: read it again (RequireStaff then sends the user to sign in). */
export function createQueryClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (err, q) => {
        if (err instanceof BffError && err.status === 401 && q.queryKey !== SESSION_KEY)
          void client.invalidateQueries({ queryKey: SESSION_KEY });
      },
    }),
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  });
  return client;
}

const toDashboard = () => '/dashboard';

export function App({ queryClient }: { queryClient: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route
          path="/login"
          element={<LoginPage landing={toDashboard} brandLogo={false} titleSuffix=" Console" showcase="console" />}
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage titleSuffix=" Console" showcase="console" />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route element={<ConsoleLayout />}>
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route element={<RequireStaff />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/cardholders" element={<CardholdersPage />} />
            <Route path="/cardholders/:id" element={<CardholderDetailPage />} />
            <Route path="/cardholders/new" element={<AddCardholderPage />} />
            <Route path="/cardholders/import" element={<BulkAddCardholdersPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/loads" element={<LoadsPage />} />
            <Route path="/points" element={<PointsToCashPage />} />
            <Route path="/points/:id" element={<PointsToCashDetailPage />} />
            <Route path="/points/settings" element={<PointsConfigurationPage />} />
            <Route path="/integrations/mdp" element={<MdpIntegrationPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route element={<RequireInternal />}>
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/partners/:id" element={<PartnerDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route path="/processor-log" element={<ProcessorLogPage />} />
              <Route path="/intercash-account" element={<InterCashAccountPage />} />
              <Route path="/funding" element={<FundingPage />} />
              {/* kiosks are on their partner's page now (PartnerDetail › Kiosks) */}
              <Route path="/kiosks" element={<Navigate to="/partners" replace />} />
              <Route path="/kiosk-simulator" element={<GscKioskPage />} />
            </Route>
          </Route>
        </Route>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}
